import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';

import styles from './VideoSection.module.css';

import { useTouchDevice } from '../../hooks';
import { Pause } from '../../icons/Pause';
import { Play } from '../../icons/Play';
import { ActivityIndicator } from '../ActivityIndicator';
import { ButtonBase } from '../ButtonBase';
import { Text } from '../Text';
import {
  type NativeWistiaOptions,
  type NativeWistiaPlayer,
  type TrackFunction,
  type VideoTrackingOptions,
  WistiaElement,
  type WistiaEventHandlers,
  useWistiaPlayer,
} from './useWistiaPlayer';

export type SectionAttributes = Pick<
  JSX.IntrinsicElements['section'],
  | 'className'
  | 'style'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-description'
  | 'aria-describedby'
  | 'aria-hidden'
>;

export interface BaseVideoSectionProps
  extends WistiaEventHandlers, SectionAttributes {
  /** The video id from wistia (can be found in the url to the video). */
  wistiaId: string;
  /**
   * Options for the Wistia embedded player.
   *
   * @see https://wistia.com/support/developers/embed-options#options-list
   */
  wistiaOptions?: NativeWistiaOptions;
  /** Content to overlay on top of video, hides when played. */
  overlayContent?: ReactNode;
  /** Content to show before video loads */
  loadingPlaceholder?: ReactNode;
  /** Block all user interaction with the video. */
  blockInteractions?: boolean;
  /** Hide the play/pause button overlay. */
  hideControls?: boolean;
  /** @deprecated Use {@link VideoSectionProps.loadingPlaceholder} */
  children?: ReactNode;
  /** Defines the title of the video. */
  videoTitle?: string | ReactNode;
  /** Controls the visibility of the placeholder overlay */
  showOverlayContent?: boolean;
  /** Whether the video is the active slide in the gallery */
  activeSlideInGallery?: boolean;
  /** Test ID for the section component, used in testing environments */
  'data-testid'?: string;
  /**
   * Defer mounting the Wistia elements & script until page is scrolled.
   *
   * @defaultValue true
   */
  deferMountUntilScroll?: boolean;
  /** Position of the overlay content. */
  overlayPosition?: 'bottom' | 'center';
  /** Background style of the overlay. */
  overlayBackground?: 'transparent' | 'dark';
  /** Whether to enforce single video playback */
  enforceSingleVideoPlayback?: boolean;
}

export interface VideoSectionPropsWithoutTracking extends BaseVideoSectionProps {
  trackingOptions?: never;
  trackFunction?: never;
}

// When using built-in tracking
export interface VideoSectionPropsWithTracking extends BaseVideoSectionProps {
  /** Function to call for tracking events - requires trackingOptions */
  trackFunction: TrackFunction;
  /** Configuration for video analytics tracking - requires trackFunction */
  trackingOptions: VideoTrackingOptions;
}

export type VideoSectionProps =
  | VideoSectionPropsWithoutTracking
  | VideoSectionPropsWithTracking;

/**
 * Render a section with a video.
 *
 * @see https://corgi-x.tfd.engineering/components/videosection
 */
export const VideoSection = forwardRef(VideoSection_);

function VideoSection_(
  props: VideoSectionProps,
  forwardedRef?: ForwardedRef<HTMLElement>
) {
  const {
    deferMountUntilScroll = true,
    overlayPosition = 'bottom',
    overlayBackground = 'transparent',
    'data-testid': dataTestId,
  } = props;

  // Track if this is a touch device
  const isTouchDevice = useTouchDevice();
  const [focused, setFocused] = useState(false);
  const [userHasPlayedVideo, setUserHasPlayedVideo] = useState(false);
  const [hidePlayButton, setHidePlayButton] = useState(false);

  // This state is used to prevent hydration mismatches between the server and client;
  // wait for the client to mount before displaying UI elements.
  const [isMounted, setIsMounted] = useState(false);

  const handleOnPause = useCallback(
    (api?: NativeWistiaPlayer) => {
      setHidePlayButton(false);
      props.onPause?.(api);
    },
    [props]
  );

  const handleOnEnd = useCallback(
    (api?: NativeWistiaPlayer) => {
      setHidePlayButton(false);
      props.onEnd?.(api);
    },
    [props]
  );

  const handleOnReady = useCallback(
    (api: NativeWistiaPlayer) => {
      // Track if user has played the video to show custom controls
      api.bind('play', () => {
        setUserHasPlayedVideo(true);
      });

      // Reset the userHasPlayedVideo state when the video ends
      api.bind('end', () => {
        setUserHasPlayedVideo(false);
      });

      props.onReady?.(api);
    },
    [props]
  );

  const player = useWistiaPlayer(
    props.wistiaId,
    props.wistiaOptions,
    props.enforceSingleVideoPlayback,
    {
      onPlay: props.onPlay,
      onPause: handleOnPause,
      onEnd: handleOnEnd,
      onReady: handleOnReady,
      onPopoverClosed: props.onPopoverClosed,
    },
    props.trackingOptions,
    props.trackFunction
  );

  // Pause video when its not the active slide and show play button
  useEffect(() => {
    if (props.activeSlideInGallery === false && player.isPlaying) {
      player.pause();
    }
  }, [props.activeSlideInGallery, player]);

  // For touch devices, hide custom controls after they've played the video.
  const shouldHideCustomControls = isTouchDevice && userHasPlayedVideo;

  const showControls =
    isMounted &&
    hidePlayButton === false &&
    !shouldHideCustomControls &&
    !props.hideControls &&
    (focused ||
      (!player.isPlaying &&
        !player.isLoading &&
        !props.wistiaOptions?.autoPlay));

  const showOverlay =
    Boolean(props.overlayContent) && props.showOverlayContent !== false;

  const ControlsIcon = player.isPlaying ? Pause : Play;

  const handleButtonClick = () => {
    if (!showControls) {
      return;
    }

    if (player.isPlaying) {
      player.pause();
    } else {
      player.play();
      if (hidePlayButton === false) {
        setHidePlayButton(true);
      }
    }
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const overlayPositionClass =
    overlayPosition === 'bottom' ? styles.overlayBottom : styles.overlayCenter;
  const overlayBackgroundClass =
    overlayBackground === 'transparent'
      ? styles.overlayBackgroundTransparent
      : styles.overlayBackgroundDark;

  return (
    <section
      ref={forwardedRef}
      className={classNames(styles.container, props.className)}
      style={props.style}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      aria-description={props['aria-description']}
      aria-describedby={props['aria-describedby']}
      aria-hidden={props['aria-hidden']}
      data-testid={dataTestId}
    >
      {props.loadingPlaceholder || props.children || (
        <div className={styles.activityIndicator}>
          <ActivityIndicator />
        </div>
      )}

      <WistiaElement
        className={styles.wistiaElement}
        id={props.wistiaId}
        options={props.wistiaOptions}
        deferMountUntilScroll={deferMountUntilScroll}
        data-testid="wistia-element"
      />

      {(showControls || showOverlay) && <div className={styles.gradient} />}

      {showOverlay && (
        <div
          className={classNames(
            styles.overlay,
            styles.showOverlay,
            overlayPositionClass,
            overlayBackgroundClass
          )}
        >
          {props.overlayContent}
        </div>
      )}

      {props.videoTitle && showControls && (
        <div className={styles.title}>
          <Text color="white" bold variant="heading-16">
            {props.videoTitle}
          </Text>
        </div>
      )}

      {isMounted && (
        <ButtonBase
          aria-label={player.isPlaying ? 'Pause Video' : 'Play Video'}
          aria-disabled={!showControls}
          onBlur={() => setFocused(false)}
          onFocus={() => setFocused(true)}
          onClick={handleButtonClick}
          className={styles.controls}
        >
          {showControls && (
            <ControlsIcon size={72} baseColor="White" aria-hidden />
          )}
        </ButtonBase>
      )}

      {props.blockInteractions && <div className={styles.interactionBlocker} />}
    </section>
  );
}

/** @deprecated Rename to {@link VideoSection}. */
export const Wistia = VideoSection;
